/**
 * Waiting-room queue types.
 *
 * The as-built droom HARDCODED position = 2 with a cosmetic local counter
 * (DATAROOM_AUDIT §1). This model is deliberately backend-driven: `position`,
 * `etaSeconds` and `total` are server values, and `source` makes the
 * "not yet loaded" state explicit so the UI shows a skeleton, never a fake.
 */

export interface QueuePosition {
  /** 1-based place in line, from the backend. null while pending. */
  position: number | null;
  /** Estimated wait in seconds, from the backend. null while pending. */
  etaSeconds: number | null;
  /** Total ahead in the same queue (for the progress bar). null while pending. */
  total: number | null;
  /** ISO timestamp of the last server update. */
  updatedAt: string | null;
  /**
   * Provenance guard. 'pending' => never render a number yet (show skeleton);
   * 'backend' => a real server value; 'ready' => AN OFFICER IS ON THE CALL, advance.
   *
   * 'ready' used to fire the moment the session finished warming, which released
   * the customer into an empty room to wait there instead of in the queue — and,
   * because the recorder arms on the LIVE transition, potentially a room with no
   * recorder yet. It now means an officer is genuinely bound to the interaction.
   */
  source: 'pending' | 'backend' | 'ready';
  /**
   * An officer is bound to the live interaction. Carried explicitly rather than
   * inferred from `source` so the waiting room can say so in words, and so a
   * client can never advance on a string it half-understands.
   */
  agentPresent: boolean;
  /**
   * Cluster #4 (customer presence): the be echoes the CUSTOMER's own presence back on the
   * heartbeat/queue read. Unused by the droom (the officer console reads it to gate the Take
   * button) — carried here for wire parity. null/undefined = an older be that does not track it.
   */
  customerPresent?: boolean | null;
  customerLastSeenAt?: string | null;
}

export const PENDING_QUEUE: QueuePosition = {
  position: null,
  etaSeconds: null,
  total: null,
  updatedAt: null,
  source: 'pending',
  agentPresent: false,
};
